/**
 * XPGainIndicator - Floating +XP animation
 *
 * Features:
 * - Shows when XP is earned
 * - Floats up and fades out
 * - Different colors for different amounts
 * - Stacks multiple gains
 * - Can be triggered programmatically
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { Zap, Star, Sparkles, Trophy } from 'lucide-react';

// XP tier thresholds
const XP_TIERS = {
  tiny: { min: 0, max: 50, color: 'text-gray-400', bg: 'bg-gray-500/20', icon: Zap },
  small: { min: 51, max: 100, color: 'text-blue-400', bg: 'bg-blue-500/20', icon: Zap },
  medium: { min: 101, max: 300, color: 'text-[#aea36d]', bg: 'bg-[#aea36d]/20', icon: Star },
  large: { min: 301, max: 500, color: 'text-yellow-400', bg: 'bg-yellow-500/20', icon: Sparkles },
  huge: { min: 501, max: Infinity, color: 'text-orange-400', bg: 'bg-orange-500/20', icon: Trophy },
};

// Get tier for XP amount
const getTier = (amount) => {
  for (const [key, tier] of Object.entries(XP_TIERS)) {
    if (amount >= tier.min && amount <= tier.max) {
      return { ...tier, key };
    }
  }
  return XP_TIERS.medium;
};

// Single floating indicator
const FloatingXP = ({ id, amount, reason, position, onComplete }) => {
  const [isVisible, setIsVisible] = useState(false);
  const tier = getTier(amount);
  const IconComponent = tier.icon;

  useEffect(() => {
    // Trigger animation
    const showTimer = setTimeout(() => setIsVisible(true), 10);

    // Auto-remove after animation
    const hideTimer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => onComplete(id), 300);
    }, 2500);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [id, onComplete]);

  return (
    <div
      className={cn(
        'fixed z-[9999] pointer-events-none',
        'transition-all duration-1000 ease-out',
        isVisible
          ? 'translate-y-[-80px] opacity-0'
          : 'translate-y-0 opacity-100'
      )}
      style={{
        left: position?.x ? `${position.x}px` : '50%',
        top: position?.y ? `${position.y}px` : '50%',
        transform: position?.x
          ? undefined
          : 'translateX(-50%)',
      }}
    >
      <div
        className={cn(
          'flex items-center gap-2 px-4 py-2 rounded-full',
          'border-2 shadow-2xl backdrop-blur-sm',
          tier.bg,
          tier.color,
          tier.key === 'huge' && 'animate-pulse',
          'border-current'
        )}
      >
        <IconComponent className="w-5 h-5" />
        <span className="text-lg font-bold">+{amount.toLocaleString()}</span>
        <span className="text-sm">XP</span>
      </div>

      {/* Reason label */}
      {reason && (
        <div
          className={cn(
            'absolute top-full left-1/2 -translate-x-1/2 mt-1',
            'px-2 py-0.5 rounded-md',
            'bg-gray-900 border border-gray-700',
            'text-xs text-gray-400 whitespace-nowrap',
            'transition-opacity duration-1000',
            isVisible ? 'opacity-0' : 'opacity-100'
          )}
        >
          {reason}
        </div>
      )}
    </div>
  );
};

// Manager component
const XPGainIndicator = ({ children }) => {
  const [gains, setGains] = useState([]);
  const [nextId, setNextId] = useState(0);

  // Show XP gain
  const showXPGain = useCallback((amount, reason = '', position = null) => {
    const id = nextId;
    setNextId((prev) => prev + 1);

    setGains((prev) => [
      ...prev,
      {
        id,
        amount,
        reason,
        position: position || {
          x: window.innerWidth / 2,
          y: window.innerHeight / 2,
        },
      },
    ]);
  }, [nextId]);

  // Remove completed gain
  const handleComplete = useCallback((id) => {
    setGains((prev) => prev.filter((g) => g.id !== id));
  }, []);

  // Expose showXPGain globally for easy access
  useEffect(() => {
    window.showXPGain = showXPGain;
    return () => {
      delete window.showXPGain;
    };
  }, [showXPGain]);

  return (
    <>
      {children}
      {createPortal(
        <>
          {gains.map((gain) => (
            <FloatingXP
              key={gain.id}
              id={gain.id}
              amount={gain.amount}
              reason={gain.reason}
              position={gain.position}
              onComplete={handleComplete}
            />
          ))}
        </>,
        document.body
      )}
    </>
  );
};

// Hook for programmatic usage
export const useXPGain = () => {
  const showXPGain = useCallback((amount, reason = '', element = null) => {
    // Get position from element or use center
    let position = null;
    if (element) {
      const rect = element.getBoundingClientRect();
      position = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      };
    }

    // Call global function if available
    if (window.showXPGain) {
      window.showXPGain(amount, reason, position);
    }
  }, []);

  return { showXPGain };
};

// Example usage component
export const XPGainTrigger = ({ amount, reason, children, onClick }) => {
  const { showXPGain } = useXPGain();

  const handleClick = (e) => {
    showXPGain(amount, reason, e.currentTarget);
    onClick?.(e);
  };

  return (
    <div onClick={handleClick} className="cursor-pointer">
      {children}
    </div>
  );
};

export default XPGainIndicator;
